import { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { postNewOmitted, deleteFavorite } from '@shared/services/services';

import { postMetaConverter } from '@shared/services/meta';
import { ID_SITE } from '@shared/conf/env';
import defaultImage from '@shared/assets/slides-noImg/no-hotel1.png';
import { HOTELS } from '@shared/helpers/routes';

const useHotelCardLogic = ({ hotel, tokenMap, hotReferenceCities, user }) => {
  const { data: integrations } = useSelector(store => store.integrations);
  const { data: providers } = useSelector(store => store.providers);

  const [isLoading, setIsLoading] = useState(false);
  const [compare, setCompare] = useState(null);
  const [isLoadImage, setIsLoadImage] = useState(false);

  const [showModalFavoritos, setShowModalFavoritos] = useState(false);
  const [showModalOmitidos, setShowModalOmitidos] = useState(false);
  const [showModalDeleteFavorite, setShowModalDeleteFavorite] = useState(false);

  const [isShowAlert, setIsShowAlert] = useState(false);
  const [alertOptions, setAlertOptions] = useState({ type: '', msg: '' });

  const [urlParams, setUrlParams] = useState({});

  useEffect(() => {
    if (user) {
      setCompare(user?.entity?.id === 23 || user?.entity?.id === 46);
    }
  }, [user]);

  useEffect(() => {
    const tokenKey = `${hotel.__isFast ? 'fast' : 'normal'}-${
      hotel.id_integration
    }`;
    const token = tokenMap.get(tokenKey);

    if (!token) {
      console.warn(
        `[useHotelCardLogic] No se encontró token para la integracion ${hotel.id_integration}`
      );
    }

    setUrlParams({
      token,
      integrationId: hotel.id_integration,
      hotelId: hotel.id_hotel,
      hotelCode: hotel.code,
      idCity: hotel.id_city,
      id_provider: hotel.id_provider || null,
    });
  }, [
    tokenMap,
    hotel.id_hotel,
    hotel.id_integration,
    hotel.code,
    hotel.id_city,
    hotel.id_provider,
  ]);

  const integration = integrations.find(i => i.id === hotel.id_integration);
  const provider = providers.find(p => p.id === hotel.id_provider);

  const retryTimeoutRef = useRef(null);
  const retryCountRef = useRef(0);

  const handleImageError = (e, main_image) => {
    e.target.src = defaultImage;

    if (retryCountRef.current < 20) {
      retryTimeoutRef.current = setTimeout(() => {
        retryCountRef.current += 1;
        e.target.src = main_image;
      }, 1500);
    } else {
      e.target.onerror = null;
    }
  };

  useEffect(() => {
    return () => {
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
      }
    };
  }, []);

  const fetchNewOmitted = async () => {
    setIsLoading(true);
    try {
      const result = await postNewOmitted({
        hotels: [
          {
            name: String(hotel.name),
            latitude: String(hotel.location.latitude),
            longitude: String(hotel.location.longitude),
          },
        ],
        hotReferenceCities,
        id_user: user.id,
        available_sites: [ID_SITE],
      });

      if (result[0].id) {
        hotel.nextSearch = true;
        hotel.nextSearchOmit = true;
        setAlertOptions({
          type: 'success',
          msg: 'Hotel omitido correctamente!',
        });
      } else {
        setAlertOptions({ type: 'error', msg: 'Hotel NO omitido!' });
      }
      setIsShowAlert(true);
    } catch (e) {
      console.error('Error omitiendo hotel', e);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchDeletedFavorite = async () => {
    setIsLoading(true);
    try {
      const result = await deleteFavorite(hotel.id_favorite);

      if (result.message === 'Borrado exitoso') {
        hotel.nextSearch = true;
        hotel.nextSearchNotFavorite = true;
        setAlertOptions({
          type: 'success',
          msg: 'Hotel eliminado de la lista de favoritos correctamente!',
        });
      } else {
        setAlertOptions({
          type: 'error',
          msg: 'Hotel NO eliminado de la lista de favoritos!',
        });
      }
      setIsShowAlert(true);
    } catch (e) {
      console.error('Error eliminando favorito', e);
    } finally {
      setIsLoading(false);
    }
  };

  const handleChangePage = () => {
    const searchFlowId = sessionStorage.getItem(
      `search-flow-id-${hotel.__isFast ? 'fast' : 'normal'}`
    );
    if (searchFlowId) {
      sessionStorage.setItem('search-flow-id', searchFlowId);
    }

    const value = {
      id_itegracion: urlParams?.integrationId,
      id_proveedor: urlParams?.id_provider,
      id_city: urlParams?.idCity,
      id_hotel: urlParams?.hotelId,
      imagen: hotel?.main_image || defaultImage,
      nombre_hotel: hotel?.name,
    };

    window.dataLayer.push({
      event: 'verDetalleHotelsMapa',
      eventProps: {
        category: 'verDetalleHotelMapa',
        action: 'ver detalle',
        label: 'ver detalle',
        value,
      },
    });

    postMetaConverter(
      {
        event_name: 'verDetalleHotelsMapa',
        custom_data: {
          category: 'verDetalleHotelMapa',
          action: 'ver detalle',
          label: 'ver detalle',
          ...value,
        },
      },
      user
    );

    window.open(
      `${HOTELS}/detail?token=${urlParams.token}&integration=${urlParams.integrationId}&hotel=${urlParams.hotelId}&hotel_code=${urlParams.hotelCode}&city=${urlParams.idCity}&id_provider=${urlParams.id_provider}`
    );
  };

  const [usdUserTooltip, setUsdUserTooltip] = useState(null);

  const [favMessage, setFavMessage] = useState(0);
  const handleFavMessage = value => setFavMessage(value);

  const hasIdFav = !!hotel?.id_favorite;

  const handleMouseEnter = (id, setFunction) => {
    setFunction(id);
  };

  const handleMouseLeave = setFunction => {
    setFunction(null);
  };

  return {
    isLoading,
    setIsLoading,
    compare,
    isLoadImage,
    setIsLoadImage,
    showModalFavoritos,
    setShowModalFavoritos,
    showModalOmitidos,
    setShowModalOmitidos,
    showModalDeleteFavorite,
    setShowModalDeleteFavorite,
    isShowAlert,
    setIsShowAlert,
    alertOptions,
    setAlertOptions,
    integration,
    provider,
    handleImageError,
    handleChangePage,
    fetchNewOmitted,
    fetchDeletedFavorite,
    usdUserTooltip,
    setUsdUserTooltip,
    favMessage,
    handleFavMessage,
    handleMouseLeave,
    hasIdFav,
    handleMouseEnter,
  };
};

export default useHotelCardLogic;
